import { UserAccessPermissions } from "@enums/UserAccessPermissions";
import { UserStatus, UserType, AccountUserType } from "@enums/UserType";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IUser, UserPlatform } from "@interfaces/IUser";
import { StateService } from "@uirouter/angularjs";
import * as angular from "angular";
import { AccountUserMappingService } from "./accountUserMapping.service.ajs";
import { CacheService } from "./cache.Service";
import { CurrentUserService } from "./currentUser.service";
import { UserMappingsService } from "./userMappings.service";

export interface IGetUsersOptions {
    includeMappings?: boolean;
    includeAdmins?: boolean;
}

export class UsersService {

    private cacheName = "usersCache";


    static $inject = ["$q", "$http", "$rootScope", "$state", "currentUserService", "accountUserMappingService", "cacheService","userMappingsService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $state: StateService,
        private readonly currentUserService: CurrentUserService,
        private readonly accountUserMappingService: AccountUserMappingService,
        private readonly cacheService: CacheService,
        private readonly userMappingsService: UserMappingsService) {

            this.cacheService.createCache(this.cacheName, {
                deleteOnExpire: "passive",
                maxAge: 60 * 60 * 1000, // 60min default
            });
    }

    createDefaultUserModel(accountId: number = 0): IUser {
        const newUser: IUser = {
            Id: 0,
            Type: UserType.Fleet,
            DriverIds: [],
            VehicleIds: [],
            GeofenceIds: [],
            GroupIds: [],
            CurrentAccountUserMapping: {
                AccountId: accountId,
                Status: UserStatus.Active,
                Type: AccountUserType.User,
                AccessMask: UserAccessPermissions.None,
                ReadFeatures: 0,
                ManageFeatures: 0,
                SpecialUserPermissions: 0
            },
            newUserLanguageCode: localStorage.getItem('userSelectedLanguageCode')??'en'
        }

        return newUser;
    }

    updateOrInsertUserCache(user: IUser): void {
        this.cacheService.addOrUpdateItem(this.cacheName, user,  (v1, v2) => v1.Id == v2.Id);
    }

    getUsers(getUsersOptions?: IGetUsersOptions, invalidateCache = false, unsafe = false): angular.IPromise<IUser[]> {
        return this.cacheService.getOrRetrieveData(this.cacheName, () => this.getUsersFromDatabase(getUsersOptions, invalidateCache), invalidateCache, unsafe);
    }

    getUsersFromDatabase(getUsersOptions?: IGetUsersOptions, invalidateCache = false): angular.IPromise<IUser[]> {

        var accountMappingPromise = this.accountUserMappingService.getAccountUserMappings(invalidateCache);

        var groupMappingPromise = this.userMappingsService.getGroupMappings(invalidateCache);

        var usersPromise = this.getUsersPromise(getUsersOptions, invalidateCache);

        var currentUserPromise = this.currentUserService.getCurrentUser(invalidateCache);

        return this.$q.all([accountMappingPromise, usersPromise, currentUserPromise, groupMappingPromise]).then(([accountMappings, users, currentUser, groupMappings]) => {

            users.forEach(user => {
                user.AccountUserMappings = accountMappings.filter(mapping => mapping.UserId == user.Id);

                user.GroupIds = groupMappings.filter(mapping => mapping.UserId == user.Id).map(m => { return m.GroupId;});

                var currentMappings = user.AccountUserMappings.filter(mapping => mapping.AccountId == currentUser.CurrentAccountUserMapping.AccountId)

                user.CurrentAccountUserMapping = currentMappings.length > 0 ? currentMappings[0] : null;
            })

            if (!getUsersOptions.includeAdmins) {
                return this.filterNonAdmins(users);
            }

            return users;
        });
    }

    private getUsersPromise(getUsersOptions?: IGetUsersOptions, invalidateCache = false): angular.IPromise<IUser[]> {

        return this.$http({
                url: "/api/users/",
                method: "GET",
                cache: true,
                params: { minsToLive: 30, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpResponse<IUser[]>) => {
                return response.data;
            });
    }

    save(user: IUser): angular.IPromise<number> {
        return this
            .$http({
                method: "POST",
                url: "/api/users/",
                data: user,
                params: { userLanguage: user.newUserLanguageCode }
            })
            .then((response: angular.IHttpResponse<number>) => {
                return response.data;
            });
    }

    deactivateUser(user: IUser): angular.IPromise<any> {
        return this.$http({
            method: "POST",
            url: "/api/users/deactivate",
            data: user
        });
    }

    reactivateUser(user: IUser): angular.IPromise<any> {
        return this.$http({
            method: "POST",
            url: "/api/users/reactivate",
            data: user.Id
        });
    }

    emailExists(emailAddress: string): angular.IPromise<boolean> {
        return this.$http(
            {
                url: `/api/users/emailexists`,
                method: "GET",
                params: { emailAddress: emailAddress }
            })
            .then((result: angular.IHttpResponse<boolean>) => {
                return result.data;
            });
    }

    getLoginToken(userId: number): angular.IPromise<string> {
        return this
            .$http({
                url: `/api/users/token/${userId}/`,
                method: "GET",
            })
            .then((result: angular.IHttpResponse<string>) => {
                return result.data;
            });
    }

    getPrimaryUserLoginToken(accountId: number): angular.IPromise<string> {
        return this
            .$http({
                url: `/api/users/token/primary/${accountId}/`,
                method: "GET",
            })
            .then((result: angular.IHttpResponse<string>) => {
                return result.data;
            });
    }

    emailBelongsToAccount(emailAddress: string, customerId: number): angular.IPromise<boolean> {
        return this.$http(
            {
                url: `/api/users/emailBelongsToAccount`,
                method: "GET",
                params: { emailAddress: emailAddress, customerId: customerId }
            })
            .then((result: angular.IHttpResponse<boolean>) => {
                return result.data;
            });
    }

    isCurrentUserInternal(): boolean {
        const currentUser: IUser = this.$rootScope.currentUser;

        return currentUser?.Type == UserType.InternalUser;
    }

    isCurrentUserOnFoundationPlatform(): boolean {
        const user: IUser = this.$rootScope.currentUser;

        if (!user) {
            return false;
        }

        return user.platform === UserPlatform.Foundation;
    }

    isInternalOrFoundation(): boolean {
        return this.isCurrentUserOnFoundationPlatform() || this.isCurrentUserInternal();
    }

    findUser(users: IUser[], userId): IUser {
        for (let i = 0; i < users.length; i++) {
            const user = users[i];

            if (user.Id === userId) {
                return user;
            }
        }

        return null;
    }

    verifyCurrentPassword(password: string) {
        return this.$http({
            method: "GET",
            url: "/api/users/verifycurrentpassword",
            params: { password: password }
        })
            .then((result: angular.IHttpResponse<boolean>) => {
                return result.data;
            });
    }

    // ----------------------------------------------

    private filterNonAdmins(users: IUser[]) {
        const newList: IUser[] = [];

        users.forEach(user => {
            if (user.CurrentAccountUserMapping && user.CurrentAccountUserMapping.Type !== AccountUserType.Admin) {
                newList.push(user);
            }
        });

        return newList;
    }
}
